"use client";

import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { DottedBorder } from "@ui/components/core/GradientBorder";
import { BaseLink } from "@ui/components/core/Link";
import { clsx } from "clsx";
import Image from "next/image";
import type { Media } from "@mw/shared/payload-types";
import { getImageProps } from "@utilities/payload";
import IconChevronRightSpiky from "@ui/central-icons/icons/IconChevronRightSpiky";
import { pxToRem } from "@ui/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ActionsItemAsset = { type: "icon"; icon: React.ReactNode } | { type: "image"; image: Media };

type ActionsItemProps =
  | {
      title: string;
      asset: ActionsItemAsset;
      href: string;
      onClick?: never;
      assetStyle?: "default";
      hoverStyle?: "default" | "brighten";
    }
  | {
      title: string;
      asset: ActionsItemAsset;
      href?: never;
      onClick: React.ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
      assetStyle?: "default";
      hoverStyle?: "default" | "brighten";
    };

type ActionsListProps = {
  items: ActionsItemProps[];
  className?: string;
  hoverStyle?: "default" | "brighten";
};

// ---------------------------------------------------------------------------
// Asset style presets
// ---------------------------------------------------------------------------

const assetStyleConfig = {
  default: {
    container: "tw-bg-gray-400 tw-rounded-[0.625rem]",
    size: "tw-size-7",
    sizes: pxToRem(28),
    ring: "tw-rounded-[0.625rem] tw-border-0.5 tw-border-black/15",
  },
} as const;

const iconStyleConfig = {
  default: {
    container:
      "tw-bg-gray-400 group-hover:tw-bg-blue-700 tw-transition-colors tw-duration-150 tw-size-full tw-flex tw-justify-center tw-items-center",
    size: "tw-size-4.5",
  },
} as const;

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function AssetSlot({ asset, style }: { asset: ActionsItemAsset; style: "default" }) {
  const config = assetStyleConfig[style];
  const iconConfig = iconStyleConfig[style];

  const imageProps = asset.type === "icon" ? undefined : getImageProps(asset.image);

  return (
    <span
      className={clsx(
        "tw-relative tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden",
        config.container,
        config.size,
      )}
    >
      {asset.type === "icon" && (
        <span className={iconConfig.container}>
          <span className={clsx("tw-flex tw-items-center tw-justify-center tw-text-white", iconConfig.size)}>
            {asset.icon}
          </span>
        </span>
      )}

      {imageProps && (
        <Image {...imageProps} alt='' className={clsx(config.size, "tw-object-cover")} sizes={config.sizes} />
      )}

      <div className={clsx("tw-absolute tw-inset-0", config.ring)} />
    </span>
  );
}

const hoverStyleConfig = {
  default: {
    text: "tw-text-black",
  },
  brighten: {
    text: "tw-text-black/60 group-hover:tw-text-black tw-transition-colors tw-duration-150",
  },
} as const;

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

export function ActionsItem({
  title,
  asset,
  assetStyle = "default",
  hoverStyle = "default",
  ...rest
}: ActionsItemProps) {
  const hoverConfig = hoverStyleConfig[hoverStyle];

  const inner = (
    <>
      <AssetSlot asset={asset} style={assetStyle} />
      <span className='tw-relative tw-min-w-0'>
        <span className={clsx("tw-block tw-truncate tw-type-xs-tight tw-py-1.5", hoverConfig.text)}>{title}</span>
        <span className='tw-opacity-0 tw-transition-opacity tw-duration-200 group-hover:tw-opacity-100'>
          <DottedBorder side='bottom' color='black' />
        </span>
      </span>
      <IconChevronRightSpiky
        className={clsx(
          iconStrokeFix,
          "tw-size-3.5 tw-shrink-0 tw-text-black",
          "tw-opacity-0 tw-translate-x-0 tw-transition-all tw-duration-200 tw-ease-out",
          "group-hover:tw-opacity-100 group-hover:tw-translate-x-1 group-focus-visible:tw-opacity-100 group-focus-visible:tw-translate-x-1",
        )}
        aria-hidden
      />
    </>
  );

  const sharedClassName =
    "tw-group tw-flex tw-items-center tw-gap-2 tw-outline-none focus-visible:tw-outline-focus tw-rounded-sm";

  if (rest.href) {
    return (
      <BaseLink href={rest.href} className={sharedClassName}>
        {inner}
      </BaseLink>
    );
  }

  return (
    <button
      type='button'
      onClick={rest.onClick}
      className={clsx(sharedClassName, "tw-cursor-pointer tw-bg-transparent")}
    >
      {inner}
    </button>
  );
}

export function ActionsList({ items, className, hoverStyle }: ActionsListProps) {
  return (
    <div className={clsx("tw-flex tw-flex-col tw-gap-3", className)}>
      {items.map((item, i) => (
        <ActionsItem key={i} hoverStyle={hoverStyle} {...item} />
      ))}
    </div>
  );
}

export type { ActionsItemProps, ActionsItemAsset, ActionsListProps };
